/**
 * The query language (SPEC §7.2), shared by the command bar's filters, views and the open-task
 * strip:
 *
 *   dyson "google analytics"          free text, phrases
 *   #meeting #task                    tags        [[Up Reply]]   references
 *   company:[[Dyson]] assignee:@me    fields (by name), `none` = no value
 *   in:journal  in:[[Up Reply]]       scope
 *   after:2026-09-01 before:… on:yesterday last:30d   dates (natural phrases in quotes)
 *   is:open is:done is:task is:ai     has:due has:audio
 *   sort:due sort:-updated limit:20
 *   a or b, (…), -term                 boolean structure; juxtaposition is "and"
 *
 * A node's date is its journal day (for blocks in the journal), else the day it was created.
 */
import { addDays, localDay, parseDatePhrase, parseSpan, type DayRange } from './dates.ts'
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { plainText } from './markdown.ts'
import type { Node } from './model.ts'
import { refsOf } from './refs.ts'
import { hasValue } from './schema.ts'
import { fieldsOf, tagsOf, type TagEnv } from './tags.ts'
import { isOpenStatus, prefixStatus, withStatus, type TaskStatus } from './tasks.ts'
import { TAG } from './builtins.ts'
import { mergedTarget } from './conflicts.ts'
import { titleKey } from './titles.ts'

export type QValue =
  | { readonly k: 'title'; readonly title: string }
  | { readonly k: 'me' }
  | { readonly k: 'none' }
  | { readonly k: 'text'; readonly v: string }

export type QNode =
  | { readonly t: 'and' | 'or'; readonly c: readonly QNode[] }
  | { readonly t: 'not'; readonly c: QNode }
  | { readonly t: 'text'; readonly v: string }
  | { readonly t: 'tag'; readonly title: string }
  | { readonly t: 'ref'; readonly v: QValue }
  | { readonly t: 'field'; readonly name: string; readonly v: QValue }
  | { readonly t: 'in'; readonly scope: QValue }
  | { readonly t: 'date'; readonly op: 'after' | 'before' | 'on'; readonly phrase: string }
  | { readonly t: 'last'; readonly days: number }
  | { readonly t: 'is'; readonly v: string }
  | { readonly t: 'has'; readonly v: string }

export interface Query {
  readonly where: QNode | null
  readonly sort: readonly { readonly key: string; readonly desc: boolean }[]
  readonly limit: number | null
  /** Parts that could not be read (shown to the user; the rest of the query still runs). */
  readonly errors: readonly string[]
}

// ------------------------------------------------------------------------------------ parsing

type Tok = { k: 'lp' } | { k: 'rp' } | { k: 'or' } | { k: 'not' } | { k: 'term'; node: QNode }

const KEY = /^([a-z][a-z0-9_.-]*):/i

export function parseQuery(src: string): Query {
  const toks: Tok[] = []
  const sort: { key: string; desc: boolean }[] = []
  let limit: number | null = null
  const errors: string[] = []
  let i = 0
  /** A value at i: [[Title]], @name, "quoted", or a run without spaces and parens. */
  const value = (): QValue | null => {
    if (src.startsWith('[[', i)) {
      const end = src.indexOf(']]', i + 2)
      if (end < 0) return null
      const title = src.slice(i + 2, end).trim()
      i = end + 2
      return { k: 'title', title }
    }
    if (src[i] === '"') {
      const end = src.indexOf('"', i + 1)
      const v = src.slice(i + 1, end < 0 ? src.length : end)
      i = end < 0 ? src.length : end + 1
      return { k: 'text', v }
    }
    const at = src[i] === '@'
    const start = at ? i + 1 : i
    if (at && src.startsWith('[[', start)) {
      i = start
      return value()
    }
    let j = start
    while (j < src.length && !/[\s()]/.test(src[j]!)) j++
    const v = src.slice(start, j)
    i = j
    if (v === '') return null
    if (at) return v.toLowerCase() === 'me' ? { k: 'me' } : { k: 'title', title: v }
    if (v.toLowerCase() === 'none') return { k: 'none' }
    return { k: 'text', v }
  }
  const text = (v: QValue): string => (v.k === 'text' ? v.v : v.k === 'title' ? v.title : v.k)

  while (i < src.length) {
    const c = src[i]!
    if (/\s/.test(c)) {
      i++
      continue
    }
    if (c === '(') {
      toks.push({ k: 'lp' })
      i++
      continue
    }
    if (c === ')') {
      toks.push({ k: 'rp' })
      i++
      continue
    }
    if (c === '-' && i + 1 < src.length && !/\s/.test(src[i + 1]!)) {
      toks.push({ k: 'not' })
      i++
      continue
    }
    if (c === '#') {
      i++
      const v = value()
      if (v && v.k !== 'none' && v.k !== 'me') toks.push({ k: 'term', node: { t: 'tag', title: text(v) } })
      continue
    }
    if (c === '@' || src.startsWith('[[', i)) {
      const v = value()
      if (v) toks.push({ k: 'term', node: { t: 'ref', v } })
      continue
    }
    if (c === '"') {
      const v = value()
      if (v) toks.push({ k: 'term', node: { t: 'text', v: text(v) } })
      continue
    }
    const key = KEY.exec(src.slice(i))
    if (key) {
      const name = key[1]!.toLowerCase()
      i += key[0].length
      const v = value()
      if (!v) {
        errors.push(`${name}: needs a value`)
        continue
      }
      const s = text(v)
      switch (name) {
        case 'sort':
          sort.push(s.startsWith('-') ? { key: s.slice(1).toLowerCase(), desc: true } : { key: s.toLowerCase(), desc: false })
          break
        case 'limit':
          if (/^\d{1,5}$/.test(s)) limit = Number(s)
          else errors.push(`limit:${s} is not a number`)
          break
        case 'in':
          toks.push({ k: 'term', node: { t: 'in', scope: v } })
          break
        case 'after':
        case 'before':
        case 'on':
          toks.push({ k: 'term', node: { t: 'date', op: name, phrase: s } })
          break
        case 'last': {
          const days = parseSpan(s)
          if (days === null) errors.push(`last:${s} (use 7d, 2w, 3m, 1y)`)
          else toks.push({ k: 'term', node: { t: 'last', days } })
          break
        }
        case 'is':
        case 'has':
          toks.push({ k: 'term', node: { t: name, v: s.toLowerCase() } })
          break
        default:
          toks.push({ k: 'term', node: { t: 'field', name, v } })
      }
      continue
    }
    let j = i
    while (j < src.length && !/[\s()]/.test(src[j]!)) j++
    const word = src.slice(i, j)
    i = j
    const lw = word.toLowerCase()
    if (lw === 'or') toks.push({ k: 'or' })
    else if (lw !== 'and') toks.push({ k: 'term', node: { t: 'text', v: word } })
  }

  let p = 0
  const parseOr = (): QNode | null => {
    const parts: QNode[] = []
    const first = parseAnd()
    if (first) parts.push(first)
    while (toks[p]?.k === 'or') {
      p++
      const next = parseAnd()
      if (next) parts.push(next)
    }
    return parts.length === 0 ? null : parts.length === 1 ? parts[0]! : { t: 'or', c: parts }
  }
  const parseAnd = (): QNode | null => {
    const parts: QNode[] = []
    while (p < toks.length && toks[p]!.k !== 'or' && toks[p]!.k !== 'rp') {
      const u = parseUnary()
      if (u) parts.push(u)
    }
    return parts.length === 0 ? null : parts.length === 1 ? parts[0]! : { t: 'and', c: parts }
  }
  const parseUnary = (): QNode | null => {
    const t = toks[p++]!
    if (t.k === 'not') {
      const u = p < toks.length ? parseUnary() : null
      return u ? { t: 'not', c: u } : null
    }
    if (t.k === 'lp') {
      const inner = parseOr()
      if (toks[p]?.k === 'rp') p++
      else errors.push('missing )')
      return inner
    }
    if (t.k === 'term') return t.node
    return null
  }
  let where = parseOr()
  while (p < toks.length) {
    // Stray ")": skip it and keep reading.
    p++
    errors.push('unmatched )')
    const more = parseOr()
    if (more) where = where ? { t: 'and', c: [where, more] } : more
  }
  return { where, sort, limit, errors }
}

// ------------------------------------------------------------------------------------ running

export interface QueryEnv extends TagEnv {
  /** Today, YYYY-MM-DD (local). */
  readonly today: string
  /** The person node standing for "me" (`@me`), if set. */
  readonly me: NodeId | null
  /** Nodes to consider (default: every live block, page and tag). */
  readonly candidates?: () => Iterable<Node>
}

/** The top-level ancestor (page, day or tag) of a node. */
function topOf(g: Graph, n: Node): Node {
  let cur = n
  for (let k = 0; k < 10_000 && cur.parentId !== null; k++) {
    const p = g.get(cur.parentId)
    if (!p) break
    cur = p
  }
  return cur
}

/** A node's day: its journal day, else the local day it was created. */
export function nodeDay(g: Graph, n: Node): string {
  const top = topOf(g, n)
  return top.kind === 'journal' && top.date ? top.date : localDay(n.createdAt)
}

/** A task's status: its prefix, or todo if it carries #task without one; null if not a task. */
export function statusOf(env: TagEnv, n: Node): TaskStatus | null {
  const p = prefixStatus(n.content)
  if (p) return p
  const task = mergedTarget(env.g, TAG.task)
  return tagsOf(env, n).includes(task) ? 'todo' : null
}

function matchStatus(status: TaskStatus | null, want: string): boolean {
  if (status === null) return want === 'none'
  if (want === 'open') return isOpenStatus(status)
  if (want === 'closed') return !isOpenStatus(status)
  return status === want
}

function fieldValueOf(env: QueryEnv, n: Node, name: string): { found: boolean; type: string; value: unknown } {
  const key = name.toLowerCase()
  for (const { field } of fieldsOf(env, n)) {
    if (field.name.toLowerCase() === key || field.id.toLowerCase() === key) return { found: true, type: field.type, value: n.props[field.id] }
  }
  return { found: false, type: '', value: undefined }
}

function matchField(env: QueryEnv, n: Node, name: string, v: QValue): boolean {
  if (name === 'status') {
    const s = statusOf(env, n)
    return s !== null && matchStatus(s, v.k === 'text' ? v.v.toLowerCase() : v.k)
  }
  const f = fieldValueOf(env, n, name)
  if (!f.found) return false
  const empty = f.value === undefined || f.value === null || f.value === '' || (Array.isArray(f.value) && f.value.length === 0)
  if (v.k === 'none') return empty
  if (empty) return false
  const values: unknown[] = Array.isArray(f.value) ? f.value : [f.value]
  if (f.type === 'ref') {
    const want = v.k === 'me' ? env.me : v.k === 'title' || v.k === 'text' ? env.resolve(v.k === 'title' ? v.title : v.v) : null
    return want !== null && values.some((x) => typeof x === 'string' && mergedTarget(env.g, x) === want)
  }
  const want = v.k === 'title' ? v.title : v.k === 'text' ? v.v : ''
  if (f.type === 'date' || f.type === 'datetime') {
    const range = parseDatePhrase(want, env.today)
    return range !== null && values.some((x) => typeof x === 'string' && x.slice(0, 10) >= range.from && x.slice(0, 10) <= range.to)
  }
  if (f.type === 'checkbox') return values.some((x) => x === /^(true|yes|1)$/i.test(want))
  if (f.type === 'text') return values.some((x) => String(x).toLowerCase().includes(want.toLowerCase()))
  return values.some((x) => String(x).toLowerCase() === want.toLowerCase())
}

function refersTo(env: QueryEnv, n: Node, target: NodeId): boolean {
  const t = env.g.get(target)
  if (t?.title) {
    const key = titleKey(t.title)
    if (refsOf(n.content).some((r) => r.title !== null && titleKey(r.title) === key)) return true
  }
  if (refsOf(n.content).some((r) => r.id === target)) return true
  for (const v of Object.values(n.props)) {
    if (v === target || (Array.isArray(v) && v.includes(target))) return true
  }
  return false
}

function within(g: Graph, n: Node, root: NodeId): boolean {
  for (let cur: Node | undefined = n; cur; cur = cur.parentId ? g.get(cur.parentId) : undefined) if (cur.id === root) return true
  return false
}

function match(env: QueryEnv, n: Node, q: QNode, ranges: Map<string, DayRange | null>): boolean {
  switch (q.t) {
    case 'and':
      return q.c.every((x) => match(env, n, x, ranges))
    case 'or':
      return q.c.some((x) => match(env, n, x, ranges))
    case 'not':
      return !match(env, n, q.c, ranges)
    case 'text': {
      const hay = `${n.title ?? ''} ${plainText(n.content)}`.toLowerCase()
      return hay.includes(q.v.toLowerCase())
    }
    case 'tag': {
      const id = q.title.toLowerCase() === 'task' ? mergedTarget(env.g, TAG.task) : env.resolve(q.title)
      return id !== null && tagsOf(env, n).includes(id)
    }
    case 'ref': {
      const id = q.v.k === 'me' ? env.me : q.v.k === 'title' ? env.resolve(q.v.title) : q.v.k === 'text' ? env.resolve(q.v.v) : null
      return id !== null && refersTo(env, n, id)
    }
    case 'field':
      return matchField(env, n, q.name, q.v)
    case 'in': {
      if (q.scope.k === 'text' && q.scope.v.toLowerCase() === 'journal') return topOf(env.g, n).kind === 'journal'
      if (q.scope.k === 'text' && q.scope.v.toLowerCase() === 'pages') return topOf(env.g, n).kind === 'page'
      const title = q.scope.k === 'title' ? q.scope.title : q.scope.k === 'text' ? q.scope.v : null
      const id = title === null ? null : env.resolve(title)
      return id !== null && id !== n.id && within(env.g, n, id)
    }
    case 'date': {
      if (!ranges.has(q.phrase)) ranges.set(q.phrase, parseDatePhrase(q.phrase, env.today))
      const r = ranges.get(q.phrase)
      if (!r) return false
      const day = nodeDay(env.g, n)
      return q.op === 'on' ? day >= r.from && day <= r.to : q.op === 'after' ? day > r.to : day < r.from
    }
    case 'last': {
      const day = nodeDay(env.g, n)
      return day >= addDays(env.today, -(q.days - 1)) && day <= env.today
    }
    case 'is':
      switch (q.v) {
        case 'open':
        case 'done':
        case 'closed':
        case 'todo':
        case 'doing':
        case 'cancelled':
          return matchStatus(statusOf(env, n), q.v)
        case 'task':
          return statusOf(env, n) !== null
        case 'ai':
          return n.origin === 'ai'
        case 'page':
        case 'block':
        case 'journal':
        case 'tag':
          return n.kind === q.v
        default:
          return false
      }
    case 'has': {
      if (q.v === 'audio' || q.v === 'transcript') return false // Phase 2
      const f = fieldsOf(env, n).find(({ field }) => field.name.toLowerCase() === q.v || field.id.toLowerCase() === q.v)
      return f !== undefined && hasValue(n.props, f.field)
    }
  }
}

/** Sort key of a node; undefined sorts last in either direction. */
function sortValue(env: QueryEnv, n: Node, key: string): string | number | undefined {
  switch (key) {
    case 'updated':
      return n.updatedAt
    case 'created':
      return n.createdAt
    case 'title':
      // Without the task prefix: tasks sort by what they say, not by their status word.
      return (n.title ?? plainText(withStatus(n.content, null))).toLowerCase()
    case 'date':
      return nodeDay(env.g, n)
    case 'status': {
      const s = statusOf(env, n)
      return s === null ? undefined : ['doing', 'todo', 'done', 'cancelled'].indexOf(s)
    }
    default: {
      const f = fieldValueOf(env, n, key)
      const v = Array.isArray(f.value) ? f.value[0] : f.value
      return typeof v === 'number' || typeof v === 'string' ? v : undefined
    }
  }
}

/** Run a query over the graph (or the given candidates). */
export function runQuery(env: QueryEnv, q: Query): Node[] {
  const out: Node[] = []
  const ranges = new Map<string, DayRange | null>()
  const nodes = env.candidates ? env.candidates() : env.g.nodes()
  for (const n of nodes) {
    if (n.deletedAt !== null || n.kind === 'asset' || n.kind === 'view') continue
    if (n.kind === 'journal' || (n.kind === 'block' && n.content === '')) continue
    if (q.where === null || match(env, n, q.where, ranges)) out.push(n)
  }
  const keys = q.sort.length > 0 ? q.sort : []
  if (keys.length > 0) {
    const cache = new Map<NodeId, (string | number | undefined)[]>()
    const vals = (n: Node) => {
      let v = cache.get(n.id)
      if (!v) cache.set(n.id, (v = keys.map((k) => sortValue(env, n, k.key))))
      return v
    }
    out.sort((a, b) => {
      const va = vals(a)
      const vb = vals(b)
      for (let i = 0; i < keys.length; i++) {
        const x = va[i]
        const y = vb[i]
        if (x === y) continue
        if (x === undefined) return 1
        if (y === undefined) return -1
        const c = x < y ? -1 : 1
        return keys[i]!.desc ? -c : c
      }
      return a.id < b.id ? -1 : 1
    })
  }
  return q.limit === null ? out : out.slice(0, q.limit)
}

/** SPEC §5.5: the open-task strip on today. */
export const OPEN_TASKS_QUERY = '#task status:open (assignee:@me or assignee:none) sort:due sort:status sort:-updated'
